import { amICoachingClientOptions } from "@/actions/client-metrics/handler";
import { useQuery } from "@tanstack/react-query";
import { Link, linkOptions, useRouteContext } from "@tanstack/react-router";
import {
  Laptop,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  Search,
  Settings,
  Sun,
  User,
  X,
} from "lucide-react";
import { useState } from "react";

import { authClient } from "@/auth/auth-client";
import { ThemeToggle, toggleTheme } from "@/components/mod-toggle";
import { PwaHeaderActions } from "@/components/pwa-banners";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const navLinks = linkOptions([
  {
    to: "/",
    label: "ANA SAYFA",
    activeOptions: { exact: true },
    viewTransition: true,
  },
  { to: "/essay", label: "İÇERİK VE HESAPLAYICILAR", viewTransition: true },
  { to: "/forum", label: "FORUM", viewTransition: true },
  { to: "/openLibrary", label: "KÜTÜPHANE", viewTransition: true },
  { to: "/program", label: "PROGRAM", viewTransition: true },
  { to: "/projects", label: "PROJELER", viewTransition: true },
  { to: "/coachs", label: "KOÇLUK", viewTransition: true },
  { to: "/store", label: "MAĞAZA", viewTransition: true },
]);

function NavLinks({
  isMobile,
  onLinkClick,
  isAuthenticated,
}: {
  isMobile?: boolean;
  onLinkClick?: () => void;
  isAuthenticated: boolean;
}) {
  const { data: coachingData } = useQuery({
    ...amICoachingClientOptions(),
    enabled: isAuthenticated,
  });
  const isCoachingClient = coachingData?.body?.isCoachingClient;

  const activeLinkStyle = { className: "text-red-500" };
  const navLinkClasses = isMobile
    ? "text-center font-semibold text-foreground text-xs uppercase tracking-wider transition-colors duration-300 hover:text-red-500"
    : "text-xs font-semibold uppercase tracking-wider transition-colors duration-300 hover:text-red-500 text-center text-foreground";

  return (
    <>
      {navLinks.map((link) => (
        <Link
          key={link.to}
          to={link.to}
          viewTransition
          activeProps={activeLinkStyle}
          className={navLinkClasses}
          onClick={onLinkClick}
        >
          {link.label}
        </Link>
      ))}
      {isCoachingClient && (
        <Link
          to="/coaching"
          viewTransition
          activeProps={activeLinkStyle}
          className={cn(navLinkClasses, "text-primary hover:text-primary/80")}
          onClick={onLinkClick}
        >
          TAKİP
        </Link>
      )}
    </>
  );
}

function UserNav() {
  const { user } = useRouteContext({ from: "/_app" });

  const initials = user?.userName?.substring(0, 2).toUpperCase() || "U";

  const handleLogout = async () => {
    await authClient.signOut();
    window.location.href = "/";
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="relative h-10 w-10 rounded-full text-foreground"
        >
          <Avatar className="h-10 w-10 border-foreground">
            <AvatarImage
              src={user?.image || ""}
              alt={user?.userName || undefined}
            />
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="font-medium text-sm leading-none">Hesabım</p>
            <p className="text-muted-foreground text-xs leading-none">
              {user?.userName || "Kullanıcı Adı"}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild className="cursor-pointer">
          <Link to="/profile">
            <User className="mr-2 h-4 w-4" />
            <span>Profil</span>
          </Link>
        </DropdownMenuItem>
        {(user?.role === "admin" || user?.role === "author") && (
          <DropdownMenuItem asChild className="cursor-pointer">
            <Link to="/admin">
              <LayoutDashboard className="mr-2 h-4 w-4" />
              <span>Admin Paneli</span>
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild className="cursor-pointer">
          <Link to="/settings">
            <Settings className="mr-2 h-4 w-4" />
            <span>Ayarlar</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="cursor-pointer">
            <Sun className="dark:-rotate-90 mr-2 h-4 w-4 rotate-0 scale-100 transition-all dark:scale-0" />
            <Moon className="absolute mr-2 h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
            <span>Tema Değiştir</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem
              onClick={() => toggleTheme("light")}
              className="cursor-pointer"
            >
              <Sun className="mr-2 h-4 w-4" />
              <span>Açık</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => toggleTheme("dark")}
              className="cursor-pointer"
            >
              <Moon className="mr-2 h-4 w-4" />
              <span>Koyu</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => toggleTheme("system")}
              className="cursor-pointer"
            >
              <Laptop className="mr-2 h-4 w-4" />
              <span>Sistem</span>
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleLogout}
          className="cursor-pointer text-red-500 focus:bg-red-500/10 focus:text-red-500"
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>Çıkış Yap</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user } = useRouteContext({ from: "/_app" });
  const isAuthenticated = !!user;

  const handleLogout = async () => {
    await authClient.signOut();
    window.location.href = "/";
  };

  return (
    <header
      // biome-ignore lint/nursery/useSortedClasses: <explanation>
      className={`absolute top-0 z-50 w-full transition-colors duration-300 border-border/50 border-b bg-background/90 backdrop-blur-sm`}
    >
      <div className="container mx-auto flex h-20 items-center justify-between gap-3.5 px-4 sm:px-6">
        <div className="shrink-0">
          <Link to="/" onClick={() => setIsMenuOpen(false)}>
            <img
              src={"/lightLogo.webp"}
              alt="Site Logosu"
              className={cn("h-24 w-24", "block dark:hidden")}
            />
            {
              <img
                src="/darkLogo.webp"
                alt="Site Logosu"
                className={cn("h-24 w-24", "hidden dark:block")}
              />
            }
          </Link>
        </div>

        <nav className="hidden items-center justify-center gap-6 lg:flex">
          <NavLinks isAuthenticated={isAuthenticated} />
        </nav>

        {/* Masaüstü: sağ aksiyonlar */}
        <div className="hidden items-center gap-3 lg:flex">
          <PwaHeaderActions />
          <Button
            variant="ghost"
            size="icon"
            className={"text-muted-foreground hover:text-foreground"}
          >
            <Search className="h-5 w-5" />
          </Button>
          {user ? (
            <UserNav />
          ) : (
            <>
              <Button size="sm" variant="destructive" asChild>
                <Link to="/password/login">Giriş Yap</Link>
              </Button>
              <div className="text-foreground">
                <ThemeToggle />
              </div>
            </>
          )}
        </div>

        {/* Mobil: PWA aksiyonları + hamburger */}
        <div className="flex items-center gap-1 lg:hidden">
          <PwaHeaderActions />
          <Button
            size="icon"
            variant="ghost"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Menüyü aç/kapat"
          >
            {isMenuOpen ? (
              <X className={`h-6 w-6 text-foreground`} />
            ) : (
              <Menu className={`h-6 w-6 text-foreground`} />
            )}
          </Button>
        </div>
      </div>

      {isMenuOpen && (
        <div className="absolute h-full w-full border-border/50 border-t lg:hidden">
          <nav className="b fade-in-80 slide-in-from-top-7 flex animate-in flex-col items-center gap-2 bg-background p-2 duration-700 ease-in-out">
            <NavLinks
              isMobile
              onLinkClick={() => setIsMenuOpen(false)}
              isAuthenticated={isAuthenticated}
            />
            <div className="mt-4 w-1/2 border-border/60 border-t" />
            <div className="flex w-full flex-col items-center gap-4 px-16 pt-6 text-foreground">
              {user ? (
                <>
                  <Button asChild variant="outline" className="w-full">
                    <Link to="/profile" onClick={() => setIsMenuOpen(false)}>
                      Profil
                    </Link>
                  </Button>
                  {(user?.role === "admin" || user?.role === "author") && (
                    <Button asChild variant="outline" className="w-full">
                      <Link to="/admin" onClick={() => setIsMenuOpen(false)}>
                        Admin Paneli
                      </Link>
                    </Button>
                  )}
                  <Button asChild variant="outline" className="w-full">
                    <Link to="/settings" onClick={() => setIsMenuOpen(false)}>
                      Ayarlar
                    </Link>
                  </Button>
                  <Button
                    variant="destructive"
                    className="w-full"
                    onClick={handleLogout}
                  >
                    Çıkış Yap
                  </Button>
                </>
              ) : (
                <Button asChild variant="outline" className="w-full">
                  <Link
                    to="/password/login"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Giriş Yap
                  </Link>
                </Button>
              )}
              <div className="w-full pt-4">
                <ThemeToggle />
              </div>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

export default Header;
